<template>
  <n-form
    :model="form"
    label-placement="top"
  >
    <div class="form-wrapper">
      <div class="form-item">
        <n-form-item
          :label="
            durationList?.length > 1
              ? translationsStore.t('booking_form_label_durations')
              : translationsStore.t('booking_form_label_duration')
          "
        >
          <n-radio-group
            v-model:value="form.duration"
            name="radiobuttongroup-duration"
          >
            <n-radio-button
              v-for="(item, index) in durationList"
              :key="index"
              :value="item"
              :label="item"
            />
          </n-radio-group>
        </n-form-item>
      </div>
      <div class="form-item">
        <n-form-item
          :label="translationsStore.t('booking_form_label_participants')"
        >
          <n-radio-group
            v-model:value="form.participants"
            name="radiobuttongroup-participant"
          >
            <n-radio-button
              v-for="(item, index) in participantList"
              :key="index"
              :value="item"
              :label="item"
            />
          </n-radio-group>
        </n-form-item>
      </div>
    </div>

    <BookingCalendar :no-match="noMatch" />
  </n-form>
</template>

<script setup>
import { ref, onMounted, watchEffect, watch, computed, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useQuery } from "villus";
import axios from "axios";
import { useBookingStore } from "../stores/booking";
import BookingCalendar from "./BookingCalendar.vue";
import { useTranslationsStore } from "../stores/translations";
import {
  graphqlFormGetProductsAndCenters,
  graphqlFormGetVariantService,
} from "../utils/queryGraphql";

const props = defineProps({
  center: {
    type: Object,
    default: () => {},
  },
  service: {
    type: Object,
    default: () => {},
  },
});

const { environment } = window.themeVariables;
const bookingStore = useBookingStore();
const translationsStore = useTranslationsStore();
const allCenters = ref(null);
const loading = ref(true);
const error = ref(null);
const centerSelected = ref(null);
const idZenotiSelectedCenter = ref(null);
const centerAllServices = ref([]);
const serviceVariants = ref([]);
const serviceVariantsIds = ref([]);
const zenotiVariantsData = ref([]);
const form = ref({
  duration: "",
  participants: "",
});
const durationFromPdp = ref(null);
const participantsFromPdp = ref(null);
const selectedVariant = ref(null);
const productVariantsByCenterId = ref([]);
const noMatch = ref(false);
const route = useRoute();
const GetProductsAndCenters = computed(() => graphqlFormGetProductsAndCenters);

const productsAndCenters = useQuery({
  query: GetProductsAndCenters,
  variables: {
    language: translationsStore.getShopifyLocale(),
  },
});

const GetVariantService = computed(() => {
  return graphqlFormGetVariantService(
    bookingStore.currentService?.id?.split("gid://shopify/Product/")[1]
  );
});

const { data } = useQuery({
  query: GetVariantService,
  variables: {
    language: translationsStore.getShopifyLocale(),
  },
});

const durationList = computed(() => {
  let list = [];
  productVariantsByCenterId.value.forEach((item) => {
    const duration = item.length === 2 ? item[1].value : item[2].value;

    list = [...list, duration];
    //remove duplicate
    list = [...new Set(list)];
    //sort by ascending duration if possible
    list.sort((a, b) => {
      const aMinutes = parseInt(a);
      const bMinutes = parseInt(b);
      return aMinutes - bMinutes;
    });
  });
  return list;
});

const participantList = computed(() => {
  let list = [];
  productVariantsByCenterId.value.forEach((item) => {
    const participant = item.length === 3 ? item[1].value : null;
    if (participant) {
      list = [...list, participant];
      //remove duplicate
      list = [...new Set(list)];
      //sort solo first
      list.sort((a, b) => {
        if (a === "Solo") return -1;
        if (b === "Solo") return 1;
        return 0;
      });
    } else {
      list = ["Solo"];
    }
  });
  return list;
});

const getVariantCurrentService = () => {
  let filteredService = [];
  console.log("getVariantCurrentService = ", serviceVariantsIds.value);
  serviceVariantsIds.value?.forEach((variant) => {
    const filteredCenterAllServices = centerAllServices.value?.filter(
      (service) => service.code === variant.zenotiCode
    );
    filteredService = [...filteredService, ...filteredCenterAllServices];
  });
  console.log("filteredService :", filteredService);
  zenotiVariantsData.value = filteredService;
};

const getZenotiCenterId = async () => {
  try {
    const response = await axios.get(
      `${environment.variables.bff_url}/pms/centers`,
      {
        headers: {
          "shopify-edge-metadata-shop-id":
            environment.variables.shopify_shop_id,
          "ngrok-skip-browser-warning":
            environment.variables.ngrok_skip_warning,
        },
      }
    );
    allCenters.value = response.data;
    centerSelected.value = allCenters.value.centers.filter(
      (center) => center.code === bookingStore.currentCenter.id_center
    )[0];
    console.log("DATA ==== ", allCenters.value);
    idZenotiSelectedCenter.value = centerSelected.value?.id;
  } catch (err) {
    error.value = "Failed to fetch data";
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const getZenotiServices = async () => {
  try {
    const response = await axios.get(
      `${environment.variables.bff_url}/pms/services?centerId=${idZenotiSelectedCenter.value}`,
      {
        headers: {
          "shopify-edge-metadata-shop-id":
            environment.variables.shopify_shop_id,
          "ngrok-skip-browser-warning":
            environment.variables.ngrok_skip_warning,
        },
      }
    );
    centerAllServices.value = response.data?.services;
    console.log("centerAllServices = ", centerAllServices);
  } catch (err) {
    error.value = "Failed to fetch data";
    console.log("getZenotiServices request error");
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const getZenotiService = async () => {
  console.log("getZenotiService", selectedVariant.value);
  try {
    if (!selectedVariant.value?.node?.metafield?.value) return;

    const response = await axios.get(
      `${environment.variables.bff_url}/pms/services/${selectedVariant.value?.node?.metafield?.value}?centerId=${bookingStore.currentCenter.id_center}`,
      {
        headers: {
          "shopify-edge-metadata-shop-id":
            environment.variables.shopify_shop_id,
          "ngrok-skip-browser-warning":
            environment.variables.ngrok_skip_warning,
        },
      }
    );
    bookingStore.updateZenotiVariantService(response.data);
    noMatch.value = false;
  } catch (err) {
    error.value = "Failed to fetch data";
    console.log("getZenotiService request error");
    if (err.status === 404) {
      noMatch.value = true;
    }
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const getShopifyVariants = () => {
  if (productsAndCenters.data.value) {
    const products =
      productsAndCenters.data.value?.collectionByHandle?.products?.edges;
    products.forEach((product) => {
      console.log("PRODUCT", product);
      console.log(
        "bookingStore.currentService?.id",
        bookingStore.currentService?.id
      );
      if (product.node.id === bookingStore.currentService?.id) {
        product.node?.variants?.edges?.forEach((variant) => {
          if (
            variant.node.selectedOptions[0]?.value ===
            bookingStore.currentCenter?.title
          ) {
            productVariantsByCenterId.value = [
              ...productVariantsByCenterId.value,
              variant.node.selectedOptions,
            ];
          }
          const bookingPdp = JSON.parse(localStorage.getItem("booking-pdp"));
          if (bookingPdp) {
            if (bookingPdp["participants"]) {
              participantsFromPdp.value = bookingPdp["participants"]?.trim();
              form.value.participants = participantsFromPdp.value;
            }

            if (bookingPdp["duration"]) {
              durationFromPdp.value = bookingPdp["duration"]?.trim();
              form.value.duration = durationFromPdp.value;
            }
          }

          if (variant.node.selectedOptions.length === 2) {
            if (
              variant.node.selectedOptions[0]?.value ===
                bookingStore.currentCenter?.title &&
              variant.node.selectedOptions[1]?.value === form.value.duration
            ) {
              selectedVariant.value = variant;
              bookingStore.$patch({ currentShopifyVariant: variant });
              getZenotiService();
            }
          } else {
            if (
              variant.node.selectedOptions[0]?.value ===
                bookingStore.currentCenter?.title &&
              variant.node.selectedOptions[1]?.value ===
                form.value.participants &&
              variant.node.selectedOptions[2]?.value === form.value.duration
            ) {
              selectedVariant.value = variant;
              bookingStore.$patch({ currentShopifyVariant: variant });
              getZenotiService();
            }
          }
        });
      }
    });
  }
};

watchEffect(() => {
  const storedBookingData = JSON.parse(localStorage.getItem("booking-storage"));
  if (!idZenotiSelectedCenter.value || !productsAndCenters.data.value) {
    return;
  }
  if (idZenotiSelectedCenter.value) {
    getZenotiServices();
  } else {
    getZenotiCenterId();
  }

  getShopifyVariants();

  if (data?.value) {
    if (data.value?.products?.nodes?.length > 1) {
      const filteredProduct = data.value?.products?.nodes?.filter(
        (product) => product.title === props.service?.title
      )[0]?.variants?.edges;
      serviceVariants.value = filteredProduct?.filter((product) =>
        product.node.selectedOptions[0]?.value === props.center?.title
          ? props.center.title
          : bookingStore.currentCenter?.title
      );
    } else if (data.value?.products?.nodes?.length === 1) {
      serviceVariants.value =
        data.value?.products?.nodes[0]?.variants?.edges?.filter((product) =>
          product.node.selectedOptions[0]?.value === props.center?.title
            ? props.center.title
            : bookingStore.currentCenter?.title
        );
    }
    serviceVariantsIds.value =
      serviceVariants.value?.map((variant) => {
        return {
          zenotiCode: variant.node.metafield?.value,
          shopify: variant.node.id,
        };
      }) ?? storedBookingData?.currentServiceVariants;
  }
});

watch([() => serviceVariantsIds.value, () => centerAllServices.value], () => {
  if (serviceVariantsIds.value?.length > 0) {
    bookingStore.updateCurrentServiceVariants(serviceVariantsIds.value);
  }
  if (
    serviceVariantsIds.value?.length > 0 &&
    centerAllServices.value?.length > 0
  ) {
    nextTick(() => {
      getVariantCurrentService();
    });
  }
});

watch(
  () => zenotiVariantsData.value,
  () => {
    if (
      zenotiVariantsData.value.length > 0 &&
      serviceVariantsIds.value.length > 0
    ) {
      serviceVariantsIds.value.forEach((variant) => {
        variant.zenotiId = zenotiVariantsData.value.find(
          (item) => item.code === variant.zenotiCode
        )?.id;
      });
    }
  }
);

watch(
  [
    () => form.value.duration,
    () => form.value.participants,
    productsAndCenters.data.value,
  ],
  () => {
    bookingStore.$patch({
      currentDuration: form.value.duration,
      currentParticipant: form.value.participants,
      currentShopifyVariant: null,
      zenotiVariantService: null,
    });
    selectedVariant.value = null;
    noMatch.value = true;
    if (productsAndCenters.data.value) {
      const products =
        productsAndCenters.data.value?.collectionByHandle?.products?.edges;

      products.forEach((product) => {
        if (product.node.id === bookingStore.currentService?.id) {
          product.node?.variants?.edges?.forEach((variant) => {
            if (
              variant.node.selectedOptions[0]?.value ===
              bookingStore.currentCenter?.title
            ) {
              productVariantsByCenterId.value = [
                ...productVariantsByCenterId.value,
                variant.node.selectedOptions,
              ];
            }

            if (variant.node.selectedOptions.length === 2) {
              if (
                variant.node.selectedOptions[0]?.value ===
                  bookingStore.currentCenter?.title &&
                variant.node.selectedOptions[1]?.value === form.value.duration
              ) {
                selectedVariant.value = variant;
                bookingStore.$patch({ currentShopifyVariant: variant });
                getZenotiService();
              }
            } else {
              if (
                variant.node.selectedOptions[0]?.value ===
                  bookingStore.currentCenter?.title &&
                variant.node.selectedOptions[1]?.value ===
                  form.value.participants &&
                variant.node.selectedOptions[2]?.value === form.value.duration
              ) {
                selectedVariant.value = variant;
                bookingStore.$patch({ currentShopifyVariant: variant });
                getZenotiService();
              }
            }
          });
        }
      });
    }
    // Updates values localstorage if change
    if (localStorage.getItem("booking-pdp")) {
      const bookingPdp = JSON.parse(localStorage.getItem("booking-pdp"));
      bookingPdp["duration"] = form.value.duration;
      bookingPdp["participants"] = form.value.participants;
      localStorage.setItem("booking-pdp", JSON.stringify(bookingPdp));
    }
  }
);

watch(
  () => durationList.value,
  (newList) => {
    setTimeout(() => {
      if (
        newList.length > 0 &&
        durationFromPdp.value === null &&
        (bookingStore.currentDuration === null ||
          bookingStore.currentDuration === "")
      ) {
        if (newList.includes("50 min")) {
          form.value.duration = "50 min";
        } else {
          form.value.duration = newList[0];
        }
      }
    }, 500);
  },
  { once: true }
);

watch(
  () => participantList.value,
  (newList) => {
    if (
      newList.length > 0 &&
      participantsFromPdp.value === null &&
      (bookingStore.currentParticipant === null ||
        bookingStore.currentParticipant === "")
    ) {
      form.value.participants = newList[0];
    }
  },
  { once: true }
);

watch(
  () => [durationFromPdp.value, participantsFromPdp.value],
  ([newDuration, newParticipants]) => {
    if (newDuration && durationList.value.includes(newDuration)) {
      form.value.duration = newDuration;
    }
    if (newParticipants && participantList.value.includes(newParticipants)) {
      form.value.participants = newParticipants;
    }
  }
);
watch(
  () => bookingStore.currentService,
  (currentService) => {
    if (currentService) {
      // console.log("✅ productsAndCenters loaded");
      console.log("currentService", currentService);

      getShopifyVariants();
    }
  },
  { immediate: true }
);
watch(
  () => bookingStore.currentCenter,
  () => {
    getZenotiCenterId();
  }
);
watch(
  () => selectedVariant.value,
  () => {
    getZenotiService();
  }
);
onMounted(async () => {
  window.scrollTo(0, 0);

  getZenotiCenterId();
  if (serviceVariantsIds.value.length > 0) {
    getVariantCurrentService();
  }

  if (!bookingStore.currentShopifyVariant?.node) {
    getShopifyVariants();
  }

  if (bookingStore.currentShopifyVariant?.node?.selectedOptions.length > 0) {
    if (bookingStore.currentDuration) {
      form.value.duration = bookingStore.currentDuration;
    }
    if (bookingStore.currentParticipant) {
      form.value.participants = bookingStore.currentParticipant;
    }
  }
  // Acces depuis la page produit avec variant dans l'URL
  if (route.query.variant) {
    console.log("direct access from product page");
    selectedVariant.value = route.query.variant;
    bookingStore.$patch({
      currentShopifyVariant: {
        node: {
          id: route.query.variant,
        },
      },
    });
  }
});
</script>

<style scoped>
.form-wrapper {
  background-color: rgb(var(--brand-neutral-2) / 1);
  padding: 1.5rem 1rem;
  margin-bottom: 1.875rem;
}
.form-item + .form-item {
  margin-top: 1.5rem;
}
@media screen and (min-width: 1000px) {
  .form-wrapper {
    display: flex;
    justify-content: space-between;
    padding: 1.5rem 2rem;
    margin-bottom: 2.375rem;
  }
  .form-item {
    width: 50%;
  }
  .form-item + .form-item {
    margin-top: 0;
  }
}
</style>
<style>
.form-wrapper .n-form-item .n-form-item-label {
  color: rgb(var(--brand-black) / 1) !important;
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}
.form-wrapper .n-radio-group .n-radio-button,
.booking-calendar .n-radio-group .n-radio-button {
  margin-right: 0.75rem;
  border: none;
  padding: 0 0.75rem;
}
.booking-calendar .n-radio-group .n-radio-button {
  padding: 0.75rem;
  font-size: 0.875rem;
  margin-right: 0;
}
.form-wrapper .n-radio-group .n-radio-button .n-radio-button__state-border,
.booking-calendar .n-radio-group .n-radio-button .n-radio-button__state-border {
  display: none;
}
.form-wrapper .n-radio-group .n-radio-button.n-radio-button--checked,
.booking-calendar .n-radio-group .n-radio-button.n-radio-button--checked {
  background-color: rgb(var(--brand-primary) / 1);
  color: rgb(var(--brand-white) / 1);
  font-weight: bold;
}
.form-wrapper .n-radio-group .n-radio-group__splitor,
.booking-calendar .n-radio-group .n-radio-group__splitor {
  width: 0;
}
.form-wrapper .n-form-item .n-form-item-feedback-wrapper,
.booking-calendar .n-form-item .n-form-item-feedback-wrapper {
  display: none;
}
.booking-calendar .toggle-button {
  text-transform: uppercase;
  margin-block-end: 0.75rem;
}
@media screen and (min-width: 1000px) {
  .form-wrapper .n-form-item .n-form-item-label {
    font-size: 1.25rem;
  }
}
</style>
